<template>
    <vx-popover v-if="hint" hover teleport arrow placement="top" class="min-w-0">
        <vx-badge data-test="terms" :color="hint.color" size="xs">
            {{ termsLabels[props.terms] }}
            <font-awesome-icon
                v-if="hint.icon"
                widthAuto
                data-test="terms-exempt-icon"
                :icon="hint.icon"
                class="text-amber-400!"
            />
        </vx-badge>
        <template v-slot:content>
            <div class="max-w-[220px] text-center">
                <div v-if="hint.title" data-test="terms-due" class="font-semibold">{{ hint.title }}</div>
                <div data-test="terms-explanation">{{ hint.explanation }}</div>
            </div>
        </template>
    </vx-popover>
    <vx-badge v-else-if="termsBadge" data-test="terms" :color="termsBadge.color" size="xs">
        {{ termsLabels[props.terms] }}
        <font-awesome-icon v-if="termsBadge.icon" widthAuto :icon="termsBadge.icon" />
    </vx-badge>
    <span v-else data-test="terms" class="text-slate-500">{{ termsLabels[props.terms] }}</span>
</template>

<script lang="ts" setup>
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCheck, faCrown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBadge, VxPopover, type VxBadgeColor } from '@voxie/frontend-components';
import { computed } from 'vue';
import type { KioskTeamGroupTerms, KioskTeamGroupTermsDue } from '~/services/adminTeamGroupsClient.types';
import { termsDueLabel, termsLabels } from './kioskTeamGroupsTableHelpers';

interface TermsBadge {
    color: VxBadgeColor;
    icon: IconDefinition | null;
}

interface Hint {
    color: VxBadgeColor;
    icon: IconDefinition | null;
    title: string;
    explanation: string;
}

const termsBadges: Record<KioskTeamGroupTerms, TermsBadge | null> = {
    accepted: { color: 'sky', icon: faCheck },
    exempt: null,
    not_accepted: { color: 'slate', icon: null },
    no_terms: null,
};

const dueExplanations: Record<Exclude<KioskTeamGroupTermsDue, ''>, Pick<Hint, 'color' | 'explanation'>> = {
    upcoming: {
        color: 'amber',
        explanation: 'The terms are not in effect yet. The owner must accept them to avoid interruptions in service.',
    },
    overdue: {
        color: 'rose',
        explanation: 'The terms are now in effect. The owner must accept them to restore service.',
    },
};

const exemptHint: Hint = {
    color: 'slate',
    icon: faCrown,
    title: '',
    explanation: 'The group owner is a corporate user, so the terms do not apply to this group.',
};

const props = withDefaults(
    defineProps<{
        terms: KioskTeamGroupTerms;
        termsDue?: KioskTeamGroupTermsDue;
    }>(),
    {
        termsDue: '',
    }
);

const termsBadge = computed(() => termsBadges[props.terms]);

const hint = computed<Hint | null>(() => {
    if (props.terms === 'exempt') {
        return exemptHint;
    }

    if (props.terms === 'not_accepted' && props.termsDue !== '') {
        return { ...dueExplanations[props.termsDue], icon: null, title: termsDueLabel(props.termsDue) };
    }

    return null;
});
</script>
